// Lyra(라이라, 궁수) 스킬 타임라인·기본 공격·각성(c022 D4a, PRD 3·3A.1, COMBAT-FEEL 2.10·2.11). 수치·판정·연출 큐의 정본이다.
// 형식은 kael.ts와 같은 SkillTimeline(types.ts). 화살은 'arrow', 장판(화살비·덫·회오리)은 'zone' 히트박스로 적고 game/arrows.ts가 실행한다.
// 시간 값은 틱(ms()로 적은 값은 밀리초 → 틱). 이 파일은 config.ts를 import하지 않는다(config가 이 파일을 import하므로 순환 방지).
// 색 언어: 청록 바람 #3fd0c0 + 은 #c8d2dc(렌더·VFX 레인).
import { ms } from '../ticks';
import { ArrowKind, LYRA_AUTO_LEARN, type ArrowSpec, type SkillTimeline, type ZoneSpec } from './types';

/** 자동 습득 레벨(LYRA_AUTO_LEARN과 같은 값): 스킬 번호 → 레벨 */
const LV = (id: number): number => LYRA_AUTO_LEARN.find((r) => r.id === id)!.level;

/** 기본 화살: 빠른 2발(1·2타) */
export const LYRA_SHOT: ArrowSpec = { kind: ArrowKind.Basic, speed: 48, range: 13, width: 1.0, pierce: false };
/** 3타 관통 강사 */
export const LYRA_SHOT_HEAVY: ArrowSpec = { kind: ArrowKind.Heavy, speed: 58, range: 16, width: 1.3, pierce: true };

/**
 * 라이라 기본 공격 3타(원거리). config.AttackSpec과 같은 모양 + arrow: 판정 첫 틱에 화살을 쏜다(근접 부채꼴 판정 없음).
 * range는 자석·표시용 사거리(m), arc는 쓰지 않는다. 3타는 마무리타(관통·넉백), 연계·캔슬 규칙은 카엘과 같다(LINK·CANCEL).
 */
export const LYRA_COMBO = [
  { windup: ms(70), active: ms(40), recovery: ms(140), chainWindow: ms(300), range: LYRA_SHOT.range, arc: 0, mult: 0.95, finisher: false, lunge: 0, arrow: LYRA_SHOT },
  { windup: ms(60), active: ms(40), recovery: ms(150), chainWindow: ms(300), range: LYRA_SHOT.range, arc: 0, mult: 1.0, finisher: false, lunge: 0, arrow: LYRA_SHOT },
  { windup: ms(160), active: ms(60), recovery: ms(300), chainWindow: 0, range: LYRA_SHOT_HEAVY.range, arc: 0, mult: 2.0, finisher: true, lunge: 0, arrow: LYRA_SHOT_HEAVY },
] as const;

/** 조준(공격 자석) 반경: 원거리라 카엘(5.5m)보다 넓게 찾는다. 몸 이동은 없다(lunge 0) */
export const LYRA_AIM = { radius: 12 } as const;

/**
 * 각성 '폭풍 화살비'(c022): 기존 각성 흐름(모으기 → 섬광 → 컷인 → 난무 → 복귀)을 그대로 쓰고 난무만 넓은 원형 다단 화살비로 바꾼다.
 * volleys·radius·mult = 난무 타격 수·반경·한 번 배율(카엘 6 × 5.0 = 30, 라이라 8 × 3.75 = 30으로 총량 같게).
 */
export const LYRA_AWAKEN = { volleys: 8, radius: 10, mult: 3.75 } as const;

const RAIN: ZoneSpec = { kind: 'rain', radius: 3.5, at: 'target', offset: 7, pulses: 7, every: 8, mult: 0.6 };
const NET: ZoneSpec = { kind: 'net', radius: 3.2, at: 'front', offset: 2.5, pulses: 1, every: 1, mult: 1.2, life: ms(6000), trigger: 1.4, root: ms(2500) };
const VORTEX: ZoneSpec = { kind: 'vortex', radius: 5, at: 'hero', offset: 0, pulses: 6, every: 10, mult: 0.5, pull: 9 };
const SKYFALL_RAIN: ZoneSpec = { kind: 'rain', radius: 5, at: 'hero', offset: 0, pulses: 5, every: 8, mult: 0.8 };

const TRIPLE_DURATION = ms(500);
const BACKSTEP_DURATION = ms(400);
const PIERCE_DURATION = ms(600);
const LAUNCH_DURATION = ms(500);
const RAIN_DURATION = ms(450);
const AIR_DURATION = ms(750);
const NET_DURATION = ms(400);
const EXPLODE_DURATION = ms(550);
const VORTEX_DURATION = ms(500);
const HOMING_DURATION = ms(700);
const SKYFALL_DURATION = ms(800);

export const LYRA_SKILLS: readonly SkillTimeline[] = [
  {
    // 3연사: 같은 길로 빠른 화살 3발(6틱 간격)
    id: 1, key: 'U', name: '3연사', clip: 'bow_triple',
    cooldown: ms(4000), mpCost: 35, duration: TRIPLE_DURATION, cancelAt: 0.5,
    unlock: { kind: 'free' },
    hitboxes: [{ startTick: 5, endTick: 17, everyTicks: 6, shape: { kind: 'arrow', arrow: { kind: ArrowKind.Basic, speed: 52, range: 14, width: 1.0, pierce: false } }, mult: 1.0 }],
    cues: [
      { tick: 0, channel: 'vfx', name: 'lyra_triple' },
      { tick: 0, channel: 'sfx', name: 'bow_draw' },
      { tick: 5, channel: 'sfx', name: 'bow_release' },
      { tick: 11, channel: 'sfx', name: 'bow_release' },
      { tick: 17, channel: 'sfx', name: 'bow_release' },
    ],
    props: {},
  },
  {
    // 백스텝 사격: 뒤로 4.5m 빠지며(회피 대용 기동, 무적 아님) 전방으로 부채꼴 3발
    id: 2, key: 'I', name: '백스텝 사격', clip: 'bow_backstep',
    cooldown: ms(6000), mpCost: 50, duration: BACKSTEP_DURATION, cancelAt: 0.5,
    unlock: { kind: 'free' },
    motion: { kind: 'dash', distance: -4.5 },
    hitboxes: [{ startTick: 6, endTick: 6, shape: { kind: 'arrow', arrow: { kind: ArrowKind.Heavy, speed: 50, range: 13, width: 1.0, pierce: false, count: 3, spreadDeg: 20 } }, mult: 1.2 }],
    cues: [
      { tick: 0, channel: 'vfx', name: 'lyra_backstep' },
      { tick: 0, channel: 'sfx', name: 'bow_draw' },
      { tick: 6, channel: 'sfx', name: 'bow_release_heavy' },
    ],
    props: {},
  },
  {
    // 관통 화살: 선딜 250ms 뒤 길 위의 적을 모두 꿰뚫는 강사. 다운된 적도 맞는다(다운 추격)
    id: 3, key: 'O', name: '관통 화살', clip: 'bow_pierce',
    cooldown: ms(7000), mpCost: 60, duration: PIERCE_DURATION, cancelAt: 0.5,
    unlock: { kind: 'free' },
    hitboxes: [{ startTick: ms(250), endTick: ms(250), shape: { kind: 'arrow', arrow: { kind: ArrowKind.Pierce, speed: 60, range: 18, width: 1.4, pierce: true } }, mult: 2.6 }],
    cues: [
      { tick: 0, channel: 'sfx', name: 'bow_draw' },
      { tick: ms(250), channel: 'vfx', name: 'lyra_pierce' },
      { tick: ms(250), channel: 'sfx', name: 'bow_release_heavy' },
      { tick: ms(250), channel: 'camera', name: 'punch_mid' },
    ],
    props: { downChase: true },
  },
  {
    // 올려쏘기(Lv5 런처): 낮게 쏜 관통 화살이 길 위의 일반 몹을 띄운다 → 공중 연사·기본 공격으로 공중 추가타 → 착지 다운 → 관통·폭발 화살로 추격
    id: 4, key: 'L', name: '올려쏘기', clip: 'bow_launch',
    cooldown: ms(6000), mpCost: 40, duration: LAUNCH_DURATION, cancelAt: 0.5,
    unlock: { kind: 'level', level: LV(4) },
    hitboxes: [{
      startTick: 9, endTick: 9,
      shape: { kind: 'arrow', arrow: { kind: ArrowKind.Launch, speed: 45, range: 9, width: 1.8, pierce: true, y: 0.6 } }, mult: 1.6,
      grants: { launch: { lift: 3.0, airTicks: ms(800), pull: 0 } },
    }],
    cues: [
      { tick: 0, channel: 'sfx', name: 'bow_draw' },
      { tick: 9, channel: 'vfx', name: 'lyra_launch_shot' },
      { tick: 9, channel: 'sfx', name: 'bow_release_heavy' },
      { tick: 9, channel: 'camera', name: 'punch_mid' },
    ],
    props: { launcher: true, downChase: true },
  },
  {
    // 바람 화살비(Lv10): 조준한 적 자리(없으면 전방 7m)에 표식 → 8틱마다 7번 낙하(다단)
    id: 5, key: 'P', name: '바람 화살비', clip: 'bow_rain',
    cooldown: ms(9000), mpCost: 35, duration: RAIN_DURATION, cancelAt: 0.5,
    unlock: { kind: 'level', level: LV(5) },
    hitboxes: [{ startTick: 8, endTick: 8, shape: { kind: 'zone', zone: RAIN }, mult: RAIN.mult }],
    cues: [
      { tick: 0, channel: 'sfx', name: 'bow_draw' },
      { tick: 8, channel: 'sfx', name: 'bow_release' },
      { tick: 0, channel: 'vfx', name: 'lyra_rain_mark', at: 'world', event: 'place' },
      { tick: 0, channel: 'sfx', name: 'arrow_rain', at: 'world', event: 'place' },
      { tick: 0, channel: 'vfx', name: 'lyra_rain_tick', at: 'world', event: 'pulse' },
    ],
    props: {},
  },
  {
    // 공중 연사(Lv15): 짧게 떠올라(1.8m) 6틱마다 5발. 띄운 적에게 맞으면 재띄움(공중 연타)
    id: 6, key: '', name: '공중 연사', clip: 'bow_air',
    cooldown: ms(8000), mpCost: 30, duration: AIR_DURATION, cancelAt: 0.5,
    unlock: { kind: 'level', level: LV(6) },
    motion: { kind: 'jump', height: 1.8 },
    hitboxes: [{ startTick: 10, endTick: 34, everyTicks: 6, shape: { kind: 'arrow', arrow: { kind: ArrowKind.Air, speed: 50, range: 11, width: 1.2, pierce: false } }, mult: 0.85 }],
    cues: [
      { tick: 0, channel: 'vfx', name: 'lyra_air_volley' },
      { tick: 0, channel: 'sfx', name: 'bow_draw' },
      { tick: 10, channel: 'sfx', name: 'bow_release' },
      { tick: 16, channel: 'sfx', name: 'bow_release' },
      { tick: 22, channel: 'sfx', name: 'bow_release' },
      { tick: 28, channel: 'sfx', name: 'bow_release' },
      { tick: 34, channel: 'sfx', name: 'bow_release' },
    ],
    props: {},
  },
  {
    // 그물 덫(Lv20): 전방 2.5m 바닥에 설치. 몹이 밟으면(1.4m) 반경 3.2m를 속박(2.5초, 엘리트 저항 50%, 보스는 그로기 때만)하고 피해
    id: 7, key: '', name: '그물 덫', clip: 'bow_trap',
    cooldown: ms(10000), mpCost: 30, duration: NET_DURATION, cancelAt: 0.5,
    unlock: { kind: 'level', level: LV(7) },
    hitboxes: [{ startTick: 8, endTick: 8, shape: { kind: 'zone', zone: NET }, mult: NET.mult }],
    cues: [
      { tick: 0, channel: 'sfx', name: 'bow_draw' },
      { tick: 0, channel: 'vfx', name: 'lyra_net_set', at: 'world', event: 'place' },
      { tick: 0, channel: 'sfx', name: 'trap_set', at: 'world', event: 'place' },
      { tick: 0, channel: 'vfx', name: 'lyra_net_snap', at: 'world', event: 'snap' },
      { tick: 0, channel: 'sfx', name: 'trap_snap', at: 'world', event: 'snap' },
      { tick: 0, channel: 'camera', name: 'punch_mid', at: 'world', event: 'snap' },
    ],
    props: {},
  },
  {
    // 폭발 화살(Lv25): 착탄(적중·사거리 끝·벽) 반경 3.2m 폭발 + 넉백(마무리 계열). 다운된 적에게도 들어간다
    id: 8, key: '', name: '폭발 화살', clip: 'bow_explode',
    cooldown: ms(8000), mpCost: 25, duration: EXPLODE_DURATION, cancelAt: 0.5,
    unlock: { kind: 'level', level: LV(8) },
    hitboxes: [{
      startTick: 12, endTick: 12,
      shape: { kind: 'arrow', arrow: { kind: ArrowKind.Explode, speed: 45, range: 15, width: 1.0, pierce: false, explode: { radius: 3.2, mult: 2.4 } } }, mult: 1.0,
      grants: { finisher: true },
    }],
    cues: [
      { tick: 0, channel: 'sfx', name: 'bow_draw' },
      { tick: 12, channel: 'sfx', name: 'bow_release_heavy' },
      { tick: 0, channel: 'vfx', name: 'lyra_explode', at: 'world', event: 'impact' },
      { tick: 0, channel: 'sfx', name: 'arrow_explode', at: 'world', event: 'impact' },
      { tick: 0, channel: 'camera', name: 'punch_heavy', at: 'world', event: 'impact' },
    ],
    props: { downChase: true },
  },
  {
    // 회오리 화살(Lv35): 느린 관통 화살이 멈춘 자리에 흡입 회오리(반경 5m, 10틱마다 6번)
    id: 9, key: '', name: '회오리 화살', clip: 'bow_vortex',
    cooldown: ms(11000), mpCost: 25, duration: VORTEX_DURATION, cancelAt: 0.5,
    unlock: { kind: 'level', level: LV(9) },
    hitboxes: [{ startTick: 10, endTick: 10, shape: { kind: 'arrow', arrow: { kind: ArrowKind.Vortex, speed: 30, range: 10, width: 1.2, pierce: true, vortex: VORTEX } }, mult: 0.8 }],
    cues: [
      { tick: 0, channel: 'sfx', name: 'bow_draw' },
      { tick: 10, channel: 'sfx', name: 'bow_release_heavy' },
      { tick: 0, channel: 'vfx', name: 'lyra_vortex', at: 'world', event: 'place' },
    ],
    props: {},
  },
  {
    // 유도 화살 폭풍(Lv45): 4틱마다 6발, 각 발은 반경 14m 안 서로 다른 적을 겨눈다(발사 순간 방향 고정)
    id: 10, key: '', name: '유도 화살 폭풍', clip: 'bow_homing',
    cooldown: ms(12000), mpCost: 25, duration: HOMING_DURATION, cancelAt: 0.5,
    unlock: { kind: 'level', level: LV(10) },
    hitboxes: [{ startTick: 8, endTick: 28, everyTicks: 4, shape: { kind: 'arrow', arrow: { kind: ArrowKind.Homing, speed: 40, range: 16, width: 1.0, pierce: false, homingRadius: 14 } }, mult: 1.1 }],
    cues: [
      { tick: 0, channel: 'vfx', name: 'lyra_homing' },
      { tick: 0, channel: 'sfx', name: 'bow_draw' },
      { tick: 8, channel: 'sfx', name: 'bow_release' },
      { tick: 16, channel: 'sfx', name: 'bow_release' },
      { tick: 24, channel: 'sfx', name: 'bow_release' },
    ],
    props: {},
  },
  {
    // 천궁 강하(Lv55): 높이 뛰어(3.2m) 착지 순간 반경 4m 강타(마무리 계열) + 발밑 화살비(반경 5m, 8틱마다 5번)
    id: 11, key: '', name: '천궁 강하', clip: 'bow_skyfall',
    cooldown: ms(14000), mpCost: 35, duration: SKYFALL_DURATION, cancelAt: 0.5,
    unlock: { kind: 'level', level: LV(11) },
    motion: { kind: 'jump', height: 3.2 },
    hitboxes: [
      { startTick: SKYFALL_DURATION - 1, endTick: SKYFALL_DURATION - 1, shape: { kind: 'circle', radius: 4 }, mult: 2.0, grants: { finisher: true } },
      { startTick: SKYFALL_DURATION - 1, endTick: SKYFALL_DURATION - 1, shape: { kind: 'zone', zone: SKYFALL_RAIN }, mult: SKYFALL_RAIN.mult },
    ],
    cues: [
      { tick: 0, channel: 'vfx', name: 'lyra_skyfall' },
      { tick: 0, channel: 'sfx', name: 'bow_draw' },
      { tick: SKYFALL_DURATION - 1, channel: 'camera', name: 'punch_heavy' },
      { tick: SKYFALL_DURATION - 1, channel: 'sfx', name: 'arrow_explode' },
      { tick: 0, channel: 'sfx', name: 'arrow_rain', at: 'world', event: 'place' },
      { tick: 0, channel: 'vfx', name: 'lyra_rain_tick', at: 'world', event: 'pulse' },
    ],
    props: {},
  },
];

// ── 전직 스킬(C10-6, PRD 3A.1 Lv30 전직 I·Lv40 전직 II, docs/SKILL-TREE.md 14절) ──
// 스킬 번호 12 = 전직 I, 13 = 전직 II(1..11은 이미 레벨 스킬이라 뒤에 붙인다). 갈래(0 윈드 스나이퍼 · 1 트랩 레인저)마다 타임라인이 다르다.
// 해금은 레벨이 아니라 전직 영수증(content/jobQuest.ts)이다: kept 비트로 들어온다(unlock.kind 'level'은 표시용 필요 레벨, LYRA_AUTO_LEARN에는 없다).
// 기존 메커니즘만 쓴다(화살 arrow·장판 zone). 윈드 스나이퍼 = 단일·관통 장거리, 트랩 레인저 = 설치·속박·다단.
// 수치 기준(카엘 13절 방식): 단일 기대 피해는 Lv45 유도 화살 폭풍(6.6)보다 크게, 초당(÷쿨)은 그 0.55보다 낮게.
const JOB_LV1 = 30;
const JOB_LV2 = 40;

/** 차지샷: 0.9초. 550ms 시위를 끝까지 당긴 뒤 굵은 관통 저격 한 발(22m, 마무리 계열 넉백) */
const CHARGE_DURATION = ms(900);
const CHARGE_FIRE = ms(550);
/** 관통 폭풍: 0.95초. 0.17초부터 6틱마다 3갈래 관통 다발 5번 → 0.77초에 굵은 관통 마무리 한 발 */
const STORM_DURATION = ms(950);
const STORM_FIRST = 10;
const STORM_EVERY = 6;
const STORM_FINAL = 46;
/** 지뢰밭: 0.5초. 전방 2.5·5·7.5m 일직선에 덫 3개(밟으면 속박 + 폭발) */
const MINE_DURATION = ms(500);
const MINE_SET = 10;
const MINE = (offset: number): ZoneSpec => ({ kind: 'net', radius: 2.8, at: 'front', offset, pulses: 1, every: 1, mult: 2.3, life: ms(8000), trigger: 1.4, root: ms(2000) });
/** 덫 연쇄: 0.6초. 조준한 적 발밑에 속박 덫 + 같은 자리 연쇄 폭발 장판(9틱마다 5번) */
const CHAIN_DURATION = ms(600);
const CHAIN_SET = 12;
const CHAIN_NET: ZoneSpec = { kind: 'net', radius: 3.6, at: 'target', offset: 6, pulses: 1, every: 1, mult: 1.5, life: ms(3000), trigger: 1.8, root: ms(2500) };
const CHAIN_BLAST: ZoneSpec = { kind: 'rain', radius: 3.6, at: 'target', offset: 6, pulses: 5, every: 9, mult: 1.3 };

/** 윈드 스나이퍼 전직 스킬(12·13) */
export const LYRA_JOB_SNIPER: readonly [SkillTimeline, SkillTimeline] = [
  {
    id: 12, key: '', name: '차지샷', clip: 'bow_charge',
    cooldown: ms(15000), mpCost: 70, duration: CHARGE_DURATION, cancelAt: 0.7,
    unlock: { kind: 'level', level: JOB_LV1 },
    hitboxes: [{
      startTick: CHARGE_FIRE, endTick: CHARGE_FIRE,
      shape: { kind: 'arrow', arrow: { kind: ArrowKind.Pierce, speed: 72, range: 22, width: 1.8, pierce: true } }, mult: 7.2,
      grants: { finisher: true },
    }],
    cues: [
      { tick: 0, channel: 'vfx', name: 'lyra_homing' },
      { tick: 0, channel: 'sfx', name: 'bow_draw' },
      { tick: ms(300), channel: 'sfx', name: 'bow_draw' },
      { tick: CHARGE_FIRE, channel: 'vfx', name: 'lyra_pierce' },
      { tick: CHARGE_FIRE, channel: 'vfx', name: 'lyra_shot_heavy' },
      { tick: CHARGE_FIRE, channel: 'sfx', name: 'bow_release_heavy' },
      { tick: CHARGE_FIRE, channel: 'camera', name: 'punch_heavy' },
    ],
    props: { downChase: true },
  },
  {
    id: 13, key: '', name: '관통 폭풍', clip: 'bow_storm',
    cooldown: ms(18000), mpCost: 85, duration: STORM_DURATION, cancelAt: 0.9,
    unlock: { kind: 'level', level: JOB_LV2 },
    hitboxes: [
      { startTick: STORM_FIRST, endTick: STORM_FIRST + STORM_EVERY * 4, everyTicks: STORM_EVERY, shape: { kind: 'arrow', arrow: { kind: ArrowKind.Pierce, speed: 62, range: 18, width: 1.2, pierce: true, count: 3, spreadDeg: 14 } }, mult: 0.85 },
      { startTick: STORM_FINAL, endTick: STORM_FINAL, shape: { kind: 'arrow', arrow: { kind: ArrowKind.Pierce, speed: 70, range: 20, width: 2.2, pierce: true } }, mult: 3.6, grants: { finisher: true } },
    ],
    cues: [
      { tick: 0, channel: 'vfx', name: 'lyra_homing' },
      { tick: 0, channel: 'sfx', name: 'bow_draw' },
      ...[0, 1, 2, 3, 4].flatMap((i) => [
        { tick: STORM_FIRST + STORM_EVERY * i, channel: 'vfx' as const, name: 'lyra_triple' },
        { tick: STORM_FIRST + STORM_EVERY * i, channel: 'sfx' as const, name: 'bow_release' },
      ]),
      { tick: STORM_FINAL, channel: 'vfx', name: 'lyra_pierce' },
      { tick: STORM_FINAL, channel: 'vfx', name: 'lyra_shot_heavy' },
      { tick: STORM_FINAL, channel: 'sfx', name: 'bow_release_heavy' },
      { tick: STORM_FINAL, channel: 'camera', name: 'punch_heavy' },
    ],
    props: { downChase: true },
  },
];

/** 트랩 레인저 전직 스킬(12·13) */
export const LYRA_JOB_RANGER: readonly [SkillTimeline, SkillTimeline] = [
  {
    id: 12, key: '', name: '지뢰밭', clip: 'bow_mines',
    cooldown: ms(15000), mpCost: 70, duration: MINE_DURATION, cancelAt: 0.5,
    unlock: { kind: 'level', level: JOB_LV1 },
    hitboxes: [2.5, 5, 7.5].map((d) => ({ startTick: MINE_SET, endTick: MINE_SET, shape: { kind: 'zone' as const, zone: MINE(d) }, mult: MINE(d).mult })),
    cues: [
      { tick: 0, channel: 'sfx', name: 'bow_draw' },
      { tick: MINE_SET, channel: 'sfx', name: 'bow_release' },
      { tick: 0, channel: 'vfx', name: 'lyra_net_set', at: 'world', event: 'place' },
      { tick: 0, channel: 'sfx', name: 'trap_set', at: 'world', event: 'place' },
      { tick: 0, channel: 'vfx', name: 'lyra_net_snap', at: 'world', event: 'snap' },
      { tick: 0, channel: 'vfx', name: 'lyra_explode', at: 'world', event: 'snap' },
      { tick: 0, channel: 'sfx', name: 'trap_snap', at: 'world', event: 'snap' },
      { tick: 0, channel: 'sfx', name: 'arrow_explode', at: 'world', event: 'snap' },
      { tick: 0, channel: 'camera', name: 'punch_mid', at: 'world', event: 'snap' },
    ],
    props: {},
  },
  {
    id: 13, key: '', name: '덫 연쇄', clip: 'bow_chain',
    cooldown: ms(18000), mpCost: 85, duration: CHAIN_DURATION, cancelAt: 0.5,
    unlock: { kind: 'level', level: JOB_LV2 },
    hitboxes: [
      { startTick: CHAIN_SET, endTick: CHAIN_SET, shape: { kind: 'zone', zone: CHAIN_NET }, mult: CHAIN_NET.mult },
      { startTick: CHAIN_SET, endTick: CHAIN_SET, shape: { kind: 'zone', zone: CHAIN_BLAST }, mult: CHAIN_BLAST.mult },
    ],
    cues: [
      { tick: 0, channel: 'sfx', name: 'bow_draw' },
      { tick: CHAIN_SET, channel: 'sfx', name: 'bow_release_heavy' },
      { tick: 0, channel: 'vfx', name: 'lyra_net_set', at: 'world', event: 'place' },
      { tick: 0, channel: 'sfx', name: 'trap_set', at: 'world', event: 'place' },
      { tick: 0, channel: 'vfx', name: 'lyra_net_snap', at: 'world', event: 'snap' },
      { tick: 0, channel: 'sfx', name: 'trap_snap', at: 'world', event: 'snap' },
      { tick: 0, channel: 'camera', name: 'punch_mid', at: 'world', event: 'snap' },
      { tick: 0, channel: 'vfx', name: 'lyra_explode', at: 'world', event: 'pulse' },
      { tick: 0, channel: 'sfx', name: 'arrow_explode', at: 'world', event: 'pulse' },
    ],
    props: {},
  },
];

/** 갈래별 라이라 스킬 표(1..13). 0 = 윈드 스나이퍼, 1 = 트랩 레인저. 앞 11개는 LYRA_SKILLS 그대로 */
export const LYRA_SKILLS_BY_JOB: readonly [readonly SkillTimeline[], readonly SkillTimeline[]] = [
  [...LYRA_SKILLS, ...LYRA_JOB_SNIPER],
  [...LYRA_SKILLS, ...LYRA_JOB_RANGER],
];
